import { useEscapeKey } from '@/hooks/useEscapeKey';
import { type ReactNode, useEffect } from 'react';
import { createPortal } from 'react-dom';

/**
 * Náhled + tisk A6 výrobního listu (105×148 mm). Obsah listu dodá volající
 * (`children`). Tiskne se jen #a6-sheet (viz @media print v styles.css) — vhodné
 * pro Zebru s nastaveným A6/label formátem. `autoPrint` = otevřít tiskový dialog
 * hned po zobrazení (používá „Uložit a tisknout").
 */
export function A6PrintModal({
  children,
  autoPrint,
  onClose,
}: {
  children: ReactNode;
  autoPrint?: boolean;
  onClose: () => void;
}) {
  useEscapeKey(onClose);

  // Tiskový dialog až po fontech a vykreslení — jinak by se stihl otevřít
  // dřív, než jsBarcode dokreslí čárový kód a načte se logo.
  useEffect(() => {
    if (!autoPrint) return;
    let cancelled = false;
    let timer = 0;
    const fire = () => {
      if (!cancelled) window.print();
    };
    const ready = document.fonts?.ready ?? Promise.resolve();
    ready.then(() => {
      if (cancelled) return;
      requestAnimationFrame(() => {
        timer = window.setTimeout(fire, 150);
      });
    });
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [autoPrint]);

  // Portál rovnou do <body>: při tisku pak stačí schovat zbytek stránky.
  // Když list zůstával uvnitř aplikace, prohlížeč tiskl výšku celé stránky —
  // z jednoho listu vylezly čtyři (jeden s obsahem, zbytek prázdný).
  return createPortal(
    <div
      id="a6-print-root"
      className="fixed inset-0 z-[60] flex flex-col items-center justify-center p-4 overflow-y-auto print:static print:block print:p-0 print:overflow-visible"
    >
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onClose}
        className="fixed inset-0 bg-black/50 cursor-default print:hidden"
      />
      <div className="relative flex flex-col items-center my-6 print:m-0 print:block">
        {/* Samotný list — jen tohle se vytiskne. */}
        {/* `relative z-10` → stín listu se kreslí přes modrý pruh (list je „nad" ním).
            Krátký stín (dosah ~7 px), ať pruh s tlačítky nepřekryje. */}
        <div
          id="a6-sheet"
          className="relative z-10 bg-white overflow-hidden shadow-[0_3px_6px_-2px_rgba(0,0,0,0.3)]"
          style={{ width: '105mm', height: '148mm' }}
        >
          {children}
        </div>
        {/* Ovládání — netiskne se. Světle modrý pruh navazuje přímo na list (bez mezery). */}
        <div className="flex w-[105mm] justify-center gap-3 rounded-b-[8px] border border-t-0 border-primary-100 bg-primary-50 px-4 py-5 shadow-card print:hidden">
          <button
            type="button"
            onClick={onClose}
            className="bg-white text-text-secondary hover:text-text border border-border text-sm font-semibold rounded-[5px] px-5 py-2 cursor-pointer transition-colors"
          >
            Zavřít
          </button>
          <button
            type="button"
            onClick={() => window.print()}
            className="bg-action hover:bg-action-dark text-white text-sm font-bold rounded-[5px] px-6 py-2 cursor-pointer transition-colors"
          >
            🖨️ Tisknout
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
